// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ProjectStatus } from '@/constants/project-status';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { Project } from '@/project/api/types';
import { Decimal } from 'decimal.js';

interface ProjectsSummary {
  countByStatus: Map<ProjectStatus, number>;
  totalsByStatus: Map<ProjectStatus, Map<IsoCurrencyCode, Decimal>>;
  currencies: IsoCurrencyCode[];
}

/**
 * Counts every project per status and sums contract value per status and
 * currency. Currencies are ranked by overall total descending so each status
 * column shares one order; a project without a contract value still counts.
 */
export function summarizeProjects(projects: Project[]): ProjectsSummary {
  const countByStatus = new Map<ProjectStatus, number>();
  const totalsByStatus = new Map<
    ProjectStatus,
    Map<IsoCurrencyCode, Decimal>
  >();
  const currencyTotals = new Map<IsoCurrencyCode, Decimal>();

  for (const project of projects) {
    const status = project.status;
    countByStatus.set(status, (countByStatus.get(status) ?? 0) + 1);

    if (project.totalContractValue) {
      const { isoCode, amount } = project.totalContractValue;
      const statusTotals =
        totalsByStatus.get(status) ?? new Map<IsoCurrencyCode, Decimal>();
      statusTotals.set(
        isoCode,
        (statusTotals.get(isoCode) ?? new Decimal(0)).plus(amount)
      );
      totalsByStatus.set(status, statusTotals);
      currencyTotals.set(
        isoCode,
        (currencyTotals.get(isoCode) ?? new Decimal(0)).plus(amount)
      );
    }
  }

  // The ISO-code tiebreak makes the order fully deterministic; a code-point
  // compare keeps it locale-independent.
  const zero = new Decimal(0);
  const currencies = Array.from(currencyTotals.keys()).sort((left, right) => {
    const byTotal = (currencyTotals.get(right) ?? zero).comparedTo(
      currencyTotals.get(left) ?? zero
    );
    if (byTotal !== 0) {
      return byTotal;
    }
    if (left < right) {
      return -1;
    }
    if (left > right) {
      return 1;
    }
    return 0;
  });

  return { countByStatus, totalsByStatus, currencies };
}
